// Role-based feature configuration
export const USER_ROLES = {
  ADMIN: 'ADMIN',
  EMPLOYEE: 'EMPLOYEE',
};

export const FEATURES = {
  DASHBOARD: 'dashboard',
  POS: 'pos',
  ORDERS: 'orders',
  SALES: 'sales',
  SALES_METRICS: 'sales_metrics',
  SALES_PROFIT: 'sales_profit',
  SALES_SUMMARY: "sales_summary",
  PREVIOUS_BALANCE: 'previous_balance',
  FINANCE: 'finance',
  PRODUCTS: 'products',
  PRODUCTS_WRITE: 'products_write', // Add/Edit products
  CUSTOMERS: 'customers',
  VENDORS: 'vendors',
  IMEI: 'imei',
  RETURNS: 'returns',
  SETTINGS: 'settings',
  REPORTS: 'reports',
  INVENTORY: 'inventory',
  EMPLOYEE_MANAGEMENT: "employee_management",
  BRANDS: 'brands',
  CATEGORIES: 'categories',
  BATCHES: 'batches',
  PURCHASES: 'purchases',
  LOW_STOCK: 'low_stock',
  TRANSFER_HISTORY: 'transfer_history',
  GODDOWN: "godown",
  TRANSFER_TO_POS: "transfer_to_pos"
};

export const PERMISSIONS = {
  READ: 'read',
  WRITE: 'write',
  DELETE: 'delete',
  EXPORT: 'export',
  PRINT: 'print'
};

// Determine the base URL based on domain (same logic as domainSlice)
const domain = window.location.hostname;
// let baseURL = "";

// if (domain === process.env.REACT_APP_WEB_PARTS_URL) {
//   baseURL = process.env.REACT_APP_PARTS_URL;
// } else if (domain === process.env.REACT_APP_WEB_ACCESSORIES_URL) {
//   baseURL = process.env.REACT_APP_ACCESSORIES_URL;
// } else if (domain === process.env.REACT_APP_WEB_MOBILE_URL) {
//   baseURL = process.env.REACT_APP_API_URL;
// } else if (domain === process.env.REACT_APP_WEB_ACCESSORIES_GODOWN_URL) {
//   baseURL = process.env.REACT_APP_ACCESSORIES_GODDOWN_URL;
// } else if (domain === process.env.REACT_APP_LOCAL_URL) {
//   baseURL = process.env.REACT_APP_ACCESSORIES_URL;
// }

const BLOCKED_FEATURES_BY_DOMAIN = {
  [process.env.REACT_APP_WEB_ACCESSORIES_URL]: [
    FEATURES.IMEI, FEATURES.RETURNS, FEATURES.GODDOWN, FEATURES.TRANSFER_TO_POS,
    FEATURES.BRANDS, FEATURES.CATEGORIES, FEATURES.BATCHES, FEATURES.PURCHASES, 
    FEATURES.PRODUCTS_WRITE
  ],
  [process.env.REACT_APP_WEB_PARTS_URL]: [
    FEATURES.GODDOWN, FEATURES.TRANSFER_TO_POS,
    FEATURES.PRODUCTS_WRITE, FEATURES.TRANSFER_HISTORY
  ],
  [process.env.REACT_APP_WEB_ACCESSORIES_GODOWN_URL]: [
    FEATURES.IMEI, FEATURES.RETURNS, FEATURES.GODDOWN,
    FEATURES.DASHBOARD, FEATURES.POS, FEATURES.ORDERS, FEATURES.SALES,
    FEATURES.FINANCE, FEATURES.CUSTOMERS, FEATURES.TRANSFER_HISTORY
  ],
  [process.env.REACT_APP_WEB_MOBILE_URL]: [
    FEATURES.TRANSFER_HISTORY,
    FEATURES.PRODUCTS_WRITE,
    FEATURES.GODDOWN,
    FEATURES.TRANSFER_TO_POS,
  ],
};

const BLOCKED_FEATURES = BLOCKED_FEATURES_BY_DOMAIN[domain] ?? [];
// Base routing configuration by domain and role
export const BASE_ROUTING_BY_DOMAIN = {
  [process.env.REACT_APP_WEB_ACCESSORIES_URL]: {
    [USER_ROLES.ADMIN]: '/dashboard',
    [USER_ROLES.EMPLOYEE]: '/pos'
  },
  [process.env.REACT_APP_WEB_PARTS_URL]: {
    [USER_ROLES.ADMIN]: '/dashboard',
    [USER_ROLES.EMPLOYEE]: '/pos'
  },
  [process.env.REACT_APP_WEB_ACCESSORIES_GODOWN_URL]: {
    [USER_ROLES.ADMIN]: '/products',
    [USER_ROLES.EMPLOYEE]: '/products'
  },
};

export const getBaseRouting = (userRole) => {
  const domainRouting = BASE_ROUTING_BY_DOMAIN[domain];
  return domainRouting?.[userRole] || '/dashboard';
};

export default BLOCKED_FEATURES;

// Role-based feature access configuration
export const ROLE_FEATURES = {
  [USER_ROLES.EMPLOYEE]: {
    [FEATURES.POS]: [PERMISSIONS.READ, PERMISSIONS.WRITE],
    [FEATURES.ORDERS]: [PERMISSIONS.READ, PERMISSIONS.WRITE, PERMISSIONS.PRINT],
    [FEATURES.SALES]: [PERMISSIONS.READ, PERMISSIONS.WRITE, PERMISSIONS.PRINT],
    [FEATURES.SETTINGS]: [PERMISSIONS.READ, PERMISSIONS.WRITE, PERMISSIONS.PRINT],
  }
};

// Helper functions
export const hasFeatureAccess = (userRole, feature) => {
  // Check if feature is blocked
  if (BLOCKED_FEATURES.includes(feature)) return false;

  if (userRole === USER_ROLES.ADMIN) return true;
  return ROLE_FEATURES[userRole]?.[feature] !== undefined;
};

export const hasPermission = (userRole, feature, permission) => {
  // Check if feature is blocked
  if (BLOCKED_FEATURES.includes(feature)) return false;

  if (userRole === USER_ROLES.ADMIN) return true;
  return ROLE_FEATURES[userRole]?.[feature]?.includes(permission) || false;
};

export const getUserFeatures = (userRole) => {
  const allFeatures = userRole === USER_ROLES.ADMIN
    ? Object.values(FEATURES)
    : Object.keys(ROLE_FEATURES[userRole] || {});

  // Filter out blocked features
  return allFeatures.filter(feature => !BLOCKED_FEATURES.includes(feature));
};

export const getFeaturePermissions = (userRole, feature) => {
  // Check if feature is blocked
  if (BLOCKED_FEATURES.includes(feature)) return [];

  if (userRole === USER_ROLES.ADMIN) return Object.values(PERMISSIONS);
  return ROLE_FEATURES[userRole]?.[feature] || [];
};

// Helper function to check if a specific feature is blocked for the current domain
export const isFeatureBlocked = (feature) => {
  return BLOCKED_FEATURES.includes(feature);
};

// Header configuration by domain and path
export const HEADER_CONFIG = {
  products: {
    [process.env.REACT_APP_WEB_ACCESSORIES_URL]: [
      { label: "Products", value: "products" },
      // { label: "Brands", value: "brands" },
      // { label: "Categories", value: "categories" },
      // { label: "Batches", value: "batches" },
      // { label: "Purchases", value: "purchases" },
      { label: "Transfer History", value: "transfer-history" },
      { label: "Low Stock", value: "low-stock" },
    ],
    [process.env.REACT_APP_WEB_PARTS_URL]: [
      { label: "Products", value: "products" },
      { label: "Brands", value: "brands" },
      { label: "Categories", value: "categories" },
      { label: "Batches", value: "batches" },
      { label: "Purchases", value: "purchases" },
      { label: "Low Stock", value: "low-stock" },
    ],
    [process.env.REACT_APP_WEB_MOBILE_URL]: [
      { label: "Products", value: "products" },
      { label: "Brands", value: "brands" },
      { label: "Categories", value: "categories" },
      { label: "Batches", value: "batches" },
      { label: "Purchases", value: "purchases" },
      { label: "Low Stock", value: "low-stock" },
    ],
    [process.env.REACT_APP_WEB_ACCESSORIES_GODOWN_URL]: [
      { label: "Products", value: "products" },
      { label: "Brands", value: "brands" },
      { label: "Categories", value: "categories" },
      { label: "Tags", value: "tags" },
      { label: "Batches", value: "batches" },
      { label: "Purchases", value: "purchases" },
      { label: "Low Stock", value: "low-stock" },
    ],
  },
  customers: [
    { label: "Customers", value: "customers" },
  ],
  finance: [
    { label: "Journal", value: "journal" },
    { label: "Reports", value: "reports" },
  ],
  returns: [
    { label: "Register", value: "register" },
    { label: "Repair Reserve", value: "repair-reserve" },
    { label: "Supplier Returns", value: "vendor-returns" },
  ],
};

export const getHeaderLabels = (pathname) => {
  if (pathname === "/products" || pathname.startsWith("/product/") ||
    pathname === "/brands" || pathname === "/category" || 
    pathname === "/transfer-history" || pathname.startsWith("/transfer-history/") ||
    pathname === "/batches" || pathname === "/purchases" ||
    pathname === "/low-stock" || pathname === "/tags" || pathname.startsWith("/tags/")) {
    return HEADER_CONFIG.products[domain] || HEADER_CONFIG.products[process.env.REACT_APP_WEB_ACCESSORIES_URL];
  }
  if (pathname === "/customers" || pathname.startsWith("/customer/")) {
    return HEADER_CONFIG.customers;
  }
  if (pathname === "/finance" || pathname.startsWith("/finance/")) {
    return HEADER_CONFIG.finance;
  }
  if (pathname.startsWith("/returns")) {
    return HEADER_CONFIG.returns;
  }
  return [];
};